import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';

const AddOnsStep = ({ onNext, onBack, bookingData, setBookingData }) => {
  const [selectedAddOns, setSelectedAddOns] = useState(bookingData?.addOns || []);

  const addOnOptions = [
    {
      id: 'transport',
      title: 'Hotel Pickup & Drop-off',
      description: 'Convenient transportation from your hotel to the tour starting point and back',
      price: 25,
      icon: 'Car',
      popular: true,
      included: ['Air-conditioned vehicle', 'Professional driver', 'Door-to-door service']
    },
    {
      id: 'lunch',
      title: 'Traditional Balinese Lunch',
      description: 'Authentic local cuisine experience at a traditional restaurant',
      price: 18,
      icon: 'UtensilsCrossed',
      popular: false,
      included: ['3-course meal', 'Vegetarian options', 'Local beverages']
    },
    {
      id: 'guide',
      title: 'Private Tour Guide',
      description: 'Dedicated English-speaking guide for personalized experience',
      price: 45,
      icon: 'User',
      popular: true,
      included: ['Certified guide', 'Historical insights', 'Photography assistance']
    },
    {
      id: 'photos',
      title: 'Professional Photography',
      description: 'Professional photographer to capture your memorable moments',
      price: 35,
      icon: 'Camera',
      popular: false,
      included: ['50+ edited photos', 'Digital delivery', 'Print-ready quality']
    },
    {
      id: 'insurance',
      title: 'Travel Insurance',
      description: 'Comprehensive coverage for peace of mind during your tour',
      price: 12,
      icon: 'Shield',
      popular: false,
      included: ['Medical coverage', 'Trip cancellation', '24/7 support']
    },
    {
      id: 'souvenir',
      title: 'Souvenir Package',
      description: 'Curated collection of authentic Balinese handicrafts and gifts',
      price: 22,
      icon: 'Gift',
      popular: false,
      included: ['Handmade crafts', 'Local textiles', 'Gift packaging']
    }
  ];

  const handleAddOnToggle = (addOnId, checked) => {
    if (checked) {
      const addOn = addOnOptions?.find(option => option?.id === addOnId);
      setSelectedAddOns([...selectedAddOns, addOn]);
    } else {
      setSelectedAddOns(selectedAddOns?.filter(addOn => addOn?.id !== addOnId));
    }
  };

  const getTotalAddOnPrice = () => {
    return selectedAddOns?.reduce((total, addOn) => total + addOn?.price, 0);
  };

  const getBasePrice = () => {
    const adults = bookingData?.adults || 2;
    const children = bookingData?.children || 0;
    const infants = bookingData?.infants || 0;
    const basePrice = 299;
    
    return (adults * basePrice) + (children * basePrice * 0.7) + (infants * basePrice * 0.1);
  };

  const getTotalPrice = () => {
    return getBasePrice() + (getTotalAddOnPrice() * (bookingData?.adults + bookingData?.children));
  };

  const handleNext = () => {
    setBookingData({
      ...bookingData,
      addOns: selectedAddOns
    });
    onNext();
  };

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <div className="grid lg:grid-cols-3 gap-8">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-semibold text-foreground mb-2">
              Enhance Your Experience
            </h2>
            <p className="text-muted-foreground">
              Add optional services to make your tour even more memorable
            </p>
          </div>

          <div className="grid gap-4">
            {addOnOptions?.map((addOn) => {
              const isSelected = selectedAddOns?.some(selected => selected?.id === addOn?.id);
              
              return (
                <div
                  key={addOn?.id}
                  className={`bg-white rounded-xl border-2 transition-all duration-200 hover:shadow-md ${
                    isSelected ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/30'
                  }`}
                >
                  <div className="p-6">
                    <div className="flex items-start space-x-4">
                      <div className="flex-shrink-0">
                        <Checkbox
                          checked={isSelected}
                          onChange={(e) => handleAddOnToggle(addOn?.id, e?.target?.checked)}
                          className="mt-1"
                        />
                      </div>
                      
                      <div className="flex-1">
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-center space-x-3">
                            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                              isSelected ? 'bg-primary text-white' : 'bg-muted text-muted-foreground'
                            }`}>
                              <Icon name={addOn?.icon} size={20} />
                            </div>
                            <div>
                              <div className="flex items-center space-x-2">
                                <h3 className="font-semibold text-foreground">{addOn?.title}</h3>
                                {addOn?.popular && (
                                  <span className="px-2 py-1 bg-accent text-white text-xs rounded-full">
                                    Popular
                                  </span>
                                )}
                              </div>
                              <p className="text-sm text-muted-foreground mt-1">
                                {addOn?.description}
                              </p>
                            </div>
                          </div>
                          
                          <div className="text-right">
                            <div className="text-lg font-semibold text-primary">
                              +${addOn?.price}
                            </div>
                            <div className="text-xs text-muted-foreground">
                              per person
                            </div>
                          </div>
                        </div>
                        
                        <div className="flex flex-wrap gap-2">
                          {addOn?.included?.map((item, index) => (
                            <div
                              key={index}
                              className="flex items-center space-x-1 text-xs text-muted-foreground"
                            >
                              <Icon name="Check" size={12} className="text-success" />
                              <span>{item}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Recommendations */}
          <div className="bg-gradient-to-r from-primary/5 to-secondary/5 rounded-xl p-6 border border-primary/20">
            <div className="flex items-start space-x-3">
              <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                <Icon name="Lightbulb" size={16} className="text-primary" />
              </div>
              <div>
                <h4 className="font-semibold text-foreground mb-2">Recommended Combination</h4>
                <p className="text-sm text-muted-foreground mb-3">
                  Most travelers choose Hotel Pickup + Private Guide for the best experience
                </p>
                <div className="flex flex-wrap gap-2">
                  <span className="px-3 py-1 bg-white rounded-full text-xs font-medium border border-primary/20">
                    Save $10 when combined
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-xl border border-border p-6 sticky top-32">
            <h3 className="text-lg font-semibold text-foreground mb-4">
              Updated Summary
            </h3>
            
            <div className="space-y-4">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Base Tour:</span>
                <span className="font-medium">${getBasePrice()?.toFixed(2)}</span>
              </div>
              
              {selectedAddOns?.length > 0 && (
                <div className="space-y-2">
                  <div className="text-sm font-medium text-foreground">Add-ons:</div>
                  {selectedAddOns?.map((addOn) => (
                    <div key={addOn?.id} className="flex items-center justify-between text-sm pl-4">
                      <span className="text-muted-foreground">{addOn?.title}</span>
                      <span className="font-medium">
                        +${(addOn?.price * (bookingData?.adults + bookingData?.children))?.toFixed(2)}
                      </span>
                    </div>
                  ))}
                </div>
              )}
              
              <div className="border-t border-border pt-4">
                <div className="flex items-center justify-between">
                  <span className="text-lg font-semibold text-foreground">New Total:</span>
                  <span className="text-xl font-bold text-primary">${getTotalPrice()?.toFixed(2)}</span>
                </div>
                {getTotalAddOnPrice() > 0 && (
                  <div className="text-xs text-success mt-1">
                    +${(getTotalAddOnPrice() * (bookingData?.adults + bookingData?.children))?.toFixed(2)} in add-ons
                  </div>
                )}
              </div>
            </div>
            
            <div className="flex space-x-3 mt-6">
              <Button
                variant="outline"
                onClick={onBack}
                className="flex-1"
                iconName="ArrowLeft"
                iconPosition="left"
              >
                Back
              </Button>
              <Button
                onClick={handleNext}
                className="flex-1"
                iconName="ArrowRight"
                iconPosition="right"
              >
                Continue
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddOnsStep;